import React, { ReactElement, useState, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames/bind';
import Text from '../../../../../atoms/Text';
import LeaderBoard from '../LeaderBoard/LeaderBoard';
import CircleCard from '../../common/CircleCard';
import ActionButton from '../../common/ActionButton';
import { GIFT_BOX_ICON } from '../../../Constants';
import { getEventsLeaderboardContent } from '../../../../../selectors/eventsLeaderBoardSelectors';
import getCurrentUser from '../../../utils/userDetails';

import styles from './index.pcss';

const cx = classNames.bind(styles);

const isPastEvent = (saleType: string, selectedIndex: number): boolean =>
  saleType === 'ACTIVE_SALE' ? selectedIndex !== 0 : true;

interface EventsLeaderboardProps {
  platform: string;
  saleType: string;
  redirectToEventWinneers: (PAGE_TYPE: string) => void;
  leaderBoardData: [];
  currentUser: Record<string, unknown>;
  shopMoreUrl: string;
}

interface MapStateToProps {
  currentUser: Record<string, unknown>;
}

const mapStateToProps = (): MapStateToProps => {
  return {
    currentUser: getCurrentUser()
  };
};
const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const EventsLeaderboard = (props: EventsLeaderboardProps): ReactElement | null => {
  const {
    platform,
    saleType,
    redirectToEventWinneers,
    leaderBoardData = [],
    currentUser,
    eventEndsIn = 0,
    shopMoreUrl
  } = props;

  if (leaderBoardData.length === 0) return null;

  const isWebPlatform = platform === 'website';
  const activeSlotClassName = isWebPlatform ? 'desk-active-slot' : 'mob-active-slot';
  const inActiveSlotClassName = isWebPlatform ? 'desk-inactive-slot' : 'mob-inactive-slot';

  const [topShoppersData, setTopShoppersData] = useState(
    getEventsLeaderboardContent(saleType, leaderBoardData, 0)
  );

  const onClickTimeRange = (event: Record<string, unknown>): void => {
    const eventData = { ...event };
    const { target: { dataset: { index = '', eventText = '' } = {} } = {} } = eventData;

    if (eventText === 'time-text' && index !== undefined && index !== null) {
      const data = getEventsLeaderboardContent(saleType, leaderBoardData, +index);
      setTopShoppersData({ ...(data || {}) });
    }
  };

  const getActiveStatusElement = (index: number): JSX.Element | null => {
    if (index === 0 && saleType === 'ACTIVE_SALE') {
      return <div className={cx(activeSlotClassName)}>ACTIVE NOW</div>;
    }
    if (index !== 0 && saleType === 'ACTIVE_SALE') {
      return <div className={cx(inActiveSlotClassName)}>&nbsp;</div>;
    }
    return null;
  };

  const {
    selectedIndex = null,
    currentDate = '',
    timeRange = [],
    offerCardData = {},
    leaderboardData = [],
    postDataType = ''
  } = topShoppersData;

  const pastEvent = isPastEvent(saleType, selectedIndex);

  return (
    <main className={cx('events-leaderboard')}>
      <div className={cx('events-leaderboard-bnr-comp')}>
        <div
          className={cx(
            'events-leaderboard-banner',
            `${platform === 'website' ? 'desk-evt-ldrboard-bnr' : ''}`
          )}
        >
          <div>
            <Text variant="T10" weight="bold" color="White">
              Top Shoppers
            </Text>
          </div>
          <div>
            <Text variant="T6" weight="bold" color="Dark_Grey">
              {currentDate}
            </Text>
          </div>
        </div>
        <img
          className={cx(
            'events-leaderboard-bnr-icon',
            `${platform === 'website' ? 'desk-evt-ldrboard-bnr-icn' : ''}`
          )}
          src={GIFT_BOX_ICON}
          alt="Top Shopper Gift Icon"
        />
      </div>
      {timeRange.length > 0 && (
        <div className={cx('events-leaderboard-timeslots')}>
          <div className={cx('time-row')} onClick={onClickTimeRange}>
            {timeRange.map((ele, i) => {
              const timerSelectedStatus = selectedIndex === i ? 'active' : 'inactive';
              return (
                <div
                  key={i}
                  data-event-text="time-text"
                  data-index={i}
                  className={cx('time-text', timerSelectedStatus)}
                >
                  {getActiveStatusElement(i)}
                  {ele}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div
        className={cx(
          'events-leaderboard-content',
          `${platform === 'website' ? 'desk-evt-ldrboard-cnt' : ''}`
        )}
      >
        <div className={cx('events-leaderboard-offer')}>
          <CircleCard {...offerCardData} />
        </div>
        <div className={cx('events-leaderboard-users')}>
          <LeaderBoard
            list={leaderboardData}
            currentUser={currentUser}
            eventEndsIn={eventEndsIn}
            highlightUser={!pastEvent}
            showWinner={pastEvent}
            shopMoreUrl={shopMoreUrl}
            saleType={saleType}
            postDataType={postDataType}
          />
        </div>
        <div className={cx('events-leaderboard-cta')}>
          <ActionButton
            btnLabel="View all Winners"
            variant="small"
            btnTransParent
            onClick={redirectToEventWinneers}
          />
        </div>
      </div>
    </main>
  );
};

export default connector(EventsLeaderboard);
